import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PageResponse, Pagination } from '@core/shared/domain';
import { QueryParams } from '@core/shared/util';
import { ArticleSearchRequest, SearchArticle } from '@mp/content-manager/artikelsuche/data-access';
import { ArticleExport } from '@mp/content-manager/exports/domain';
import { DeletedFavoriteListArticle } from '@mpcm/shared';

import { CreateFavoriteListDto, FavoritesList, UpdateFavoriteListDto } from '../models';

@Injectable()
export class FavoritesService {
  private readonly baseUrl = '/api/contentmanagement/favorites';

  constructor(private readonly http: HttpClient) {}

  export(listId: string, exportType: string): Observable<ArticleExport> {
    return this.http.post<ArticleExport>(
      `api/contentmanagement/article-exports/${exportType}/favorites`,
      JSON.stringify(listId),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
  }

  getAllLists(): Observable<FavoritesList[]> {
    return this.http.get<FavoritesList[]>(`${this.baseUrl}`);
  }

  getAllArticles(id: string, pagination?: Pagination): Observable<PageResponse<SearchArticle>> {
    return this.http.get<PageResponse<SearchArticle>>(`${this.baseUrl}/${id}/favoritearticles`, {
      params: QueryParams.build<SearchArticle[]>()
        .page(pagination?.page ?? 1)
        .pageSize(pagination?.pageSize ?? 50)
        .toHttpParams(),
    });
  }

  getDeletedArticles(
    id: string,
    searchTerm: string,
    pagination: Partial<Pagination> = {},
  ): Observable<PageResponse<DeletedFavoriteListArticle>> {
    const { page = 1, pageSize = 50 } = pagination;
    const params = QueryParams.build<DeletedFavoriteListArticle>()
      .page(page)
      .pageSize(pageSize)
      .param('searchText', searchTerm || undefined)
      .toHttpParams();

    return this.http.get<PageResponse<DeletedFavoriteListArticle>>(`${this.baseUrl}/${id}/favoritearticles/deleted`, {
      params,
    });
  }

  removeArticleFromList(listId: string, globalArticleId: string): Observable<void> {
    const params = QueryParams.build().param('globalArticleId', globalArticleId).toHttpParams();
    return this.http.delete<void>(`${this.baseUrl}/${listId}/favoritearticles`, { params });
  }

  removeDeletedArticleFromList(listId: string, globalArticleId: string): Observable<void> {
    const params = QueryParams.build().param('globalArticleId', globalArticleId).toHttpParams();
    return this.http.delete<void>(`${this.baseUrl}/${listId}/deleted/delete`, { params });
  }

  removeAllDeletedArticlesFromList(listId: string, searchTerm: string): Observable<void> {
    const params = QueryParams.build<DeletedFavoriteListArticle>()
      .param('searchText', searchTerm || undefined)
      .toHttpParams();
    return this.http.delete<void>(`${this.baseUrl}/${listId}/deleted/delete`, { params });
  }

  createList(dto: CreateFavoriteListDto): Observable<FavoritesList> {
    return this.http.post<{ data: FavoritesList }>(`${this.baseUrl}`, dto).pipe(map(({ data }) => data));
  }

  updateList(id: string, dto: UpdateFavoriteListDto): Observable<FavoritesList> {
    return this.http.put<{ data: FavoritesList }>(`${this.baseUrl}/${id}`, dto).pipe(map(({ data }) => data));
  }

  deleteList(id: string): Observable<FavoritesList> {
    return this.http.delete<FavoritesList>(`${this.baseUrl}/${id}`);
  }

  addArticleToList(id: string, articles: string[]): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/${id}/favoritearticles`, articles);
  }

  addAllArticlesToList(id: string, request: ArticleSearchRequest): Observable<unknown> {
    const params = this.buildQueryParams(request)
      .page(request.page ?? 1)
      .pageSize(request.pageSize ?? 50)
      .toHttpParams();

    return this.http.post<unknown>(`${this.baseUrl}/${id}/searchresult`, null, {
      params,
    });
  }

  // TODO: try to move into more generic place
  private buildQueryParams(request: ArticleSearchRequest): QueryParams<SearchArticle> {
    return QueryParams.build<SearchArticle>()
      .param('query', request.query)
      .param('suppliers', request.suppliers)
      .param('manufacturers', request.manufacturers)
      .param('eClassFeatures', request.eClassFeatures)
      .param('articleFeatures', request.articleFeatures)
      .param('pharmaceuticalFeatures', request.pharmaceuticalFeatures)
      .param('category', request.category)
      .param('articleType', request.articleType)
      .param('articleMatch', request.articleMatch);
  }
}
